import React, { useState } from "react";
import { FormControlLabel, IconButton, Typography } from "@mui/material";
import { FormProvider, useForm } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";
import * as yup from "yup";
import { AddBox } from "@mui/icons-material";
import CloseIcon from "@mui/icons-material/Close";
import ActionButton from "controls/global/action-button/ActionButton";
import TextInputField from "controls/global/text-input-field";
import SelectField from "controls/global/select-field";
import DatePickerField from "controls/global/datepicker";
import NumberInputFieldPricing from "controls/global/number-input-field/pricing/NumberInputFieldPricing";
import StewartCheckbox from "controls/global/stewart-checkbox/StewartCheckbox";
import { showToast } from "controls/global/stewart-toast";
import { formatDateToJSONString } from "utils/shared";
import AgencyLocationField from "pages/components/AgencyLocationField";
import { CompanyLocation } from "entities/UIModel/CompanyLocation";
import FieldError from "controls/global/field-error/FieldError";
import {
  StyledDialog,
  StyledDialogTitle,
  StyledDescriptionTypography,
  IconContainer,
  CloseIconContainer,
  StyledDialogContent,
  FieldsGrid,
  FullWidthCell,
  FieldCell,
  RequiredFieldCell,
  DialogFooter,
} from "../shared/SharedDialogStyles";
import {
  numberField,
  commentsField,
  getCommentsError,
  YEAR_MONTH_FORMAT_ERROR,
  YEAR_MONTH_REGEX,
  splitYearMonth,
  toAccountingPeriod,
  toFirstOfMonthISO,
} from "../shared/dialogHelper";
import { usePremiumReceivableTypeOptions } from "utils/custom-hooks/usePremiumReceivableTypeOptions";
import { addPremiumReceivable } from "utils/api/premiumReceivableApi";

interface AddPremiumReceivablesProps {
  open: boolean;
  onClose: () => void;
}

export interface AddPremiumReceivables {
  agent: string;
  accountPeriod: string;
  receivedDate: Date | null;
  transactionDate: Date | null;
  policyMonth: string;
  transactionType: string;
  policies: number;
  sIPolicies: number;
  liability: number;
  sILiability: number;
  grossPremium: number;
  remitBasis: number;
  uwRetention: string;
  isAccrual: boolean;
  comments: string;
}

const SI_LIABILITY_REQUIRED_ERROR =
  "S.I. Liability is required when # S.I. Policies > 0";

const addPremiumReceivablesSchema = yup.object({
  agent: yup.string().required("Agent is required"),
  accountPeriod: yup
    .string()
    .required("Required")
    .matches(YEAR_MONTH_REGEX, YEAR_MONTH_FORMAT_ERROR),
  receivedDate: yup
    .date()
    .nullable()
    .required("Required")
    .typeError("Invalid date"),
  transactionDate: yup
    .date()
    .required("Required")
    .typeError("Invalid date"),
  policyMonth: yup
    .string()
    .nullable()
    .test(
      "policy-month-format",
      YEAR_MONTH_FORMAT_ERROR,
      (val) => !val || YEAR_MONTH_REGEX.test(val ?? ""),
    ),
  transactionType: yup.string().required("Required"),
  policies: numberField().moreThan(0, "Required"),
  sIPolicies: numberField().moreThan(0, "Required"),
  liability: numberField(),
  sILiability: numberField().test(
    "si-liability-required",
    SI_LIABILITY_REQUIRED_ERROR,
    function (val) {
      return !(this.parent.sIPolicies > 0 && !((val ?? 0) > 0));
    },
  ),
  grossPremium: numberField(),
  remitBasis: numberField(),
  uwRetention: yup.string().required("Required"),
  isAccrual: yup.boolean(),
  comments: commentsField(),
});

const initialValues: AddPremiumReceivables = {
  agent: "",
  accountPeriod: "",
  receivedDate: null,
  transactionDate: null,
  policyMonth: "",
  transactionType: "",
  policies: 0,
  sIPolicies: 0,
  liability: 0,
  sILiability: 0,
  grossPremium: 0,
  remitBasis: 0,
  uwRetention: "",
  isAccrual: false,
  comments: "",
};

const AddPremiumReceivablesModal = ({
  open,
  onClose,
}: AddPremiumReceivablesProps) => {
  const [agentLocation, setAgentLocation] = useState<CompanyLocation>();
  const transactionTypeOptions = usePremiumReceivableTypeOptions();
  const methods = useForm<AddPremiumReceivables>({
    mode: "onBlur",
    defaultValues: initialValues,
    resolver: yupResolver(addPremiumReceivablesSchema) as any,
  });

  const { handleSubmit, register, reset, watch, setValue, formState } = methods;
  const { errors } = formState;

  const commentsError = getCommentsError(
    watch("comments"),
    errors.comments?.message,
  );

  const handleClose = () => {
    setAgentLocation(undefined);
    reset(initialValues);
    onClose();
  };

  const clearAgentLocation = () => {
    setValue("agent", "");
    setAgentLocation(undefined);
  };

  const handleAgentChange = (location: CompanyLocation | string) => {
    if (!location || typeof location === "string") return;
    setValue("agent", location?.locationFullDisplayName || "");
    setAgentLocation(location);
  };

  const handleAgentBlur = (event: React.ChangeEvent<any>) => {
    const newInputValue = event?.target?.value;
    if (
      agentLocation === undefined ||
      !newInputValue ||
      newInputValue.toLowerCase() !==
        agentLocation?.locationFullDisplayName?.toLowerCase()
    ) {
      clearAgentLocation();
    }
  };

  const onSubmit = async (values: AddPremiumReceivables) => {
    const policyPeriod = values.policyMonth
      ? splitYearMonth(values.policyMonth)
      : null;

    const saved = await addPremiumReceivable({
      AgentID: agentLocation?.locationLegacyID,
      AccountingPeriod: toAccountingPeriod(values.accountPeriod),
      ReceivedDate: formatDateToJSONString(values.receivedDate, true) as string,
      TransactionDate: formatDateToJSONString(
        values.transactionDate,
        true,
      ) as string,
      PolicyDate: values.policyMonth
        ? toFirstOfMonthISO(values.policyMonth)
        : undefined,
      PolicyYear: policyPeriod?.year,
      PolicyMonth: policyPeriod?.month,
      PremiumReceivableTypeCode: values.transactionType,
      PolicyCount: values.policies,
      SIPolicyCount: values.sIPolicies,
      LiabilityAmount: values.liability,
      SILiabilityAmount: values.sILiability,
      ActualFee: values.grossPremium,
      ActualRiskRate: values.remitBasis,
      ActualRemittance: Number(values.uwRetention),
      IsAccrual: values.isAccrual,
      Comments: values.comments,
    });

    if (!saved) {
      showToast({
        message: "Failed to add the Premium Receivable entry. Please try again.",
        type: "error",
      });
      return;
    }

    showToast({
      message: `Premium Receivable entry added successfully`,
      type: "success",
    });
    handleClose();
  };

  return (
    <StyledDialog open={open}>
      <FormProvider {...methods}>
        <StyledDialogTitle>
          <IconContainer>
            <AddBox />
          </IconContainer>
          <Typography>Add Premium Receivable Entry</Typography>
          <CloseIconContainer>
            <IconButton className="iconButton" onClick={handleClose}>
              <CloseIcon />
            </IconButton>
          </CloseIconContainer>
        </StyledDialogTitle>

        <StyledDialogContent>
          <StyledDescriptionTypography>
            Manually create a single PR row. Rarely needed — most entries come
            from the close process or the bulk upload.
          </StyledDescriptionTypography>

          <FieldsGrid>
            <FullWidthCell>
              <RequiredFieldCell>
                <AgencyLocationField
                  label="Agent"
                  name="agent"
                  onChange={handleAgentChange}
                  onBlur={handleAgentBlur}
                  onClear={clearAgentLocation}
                />
                <FieldError message={errors.agent?.message} />
              </RequiredFieldCell>
            </FullWidthCell>

            <RequiredFieldCell>
              <TextInputField
                name="accountPeriod"
                label="Accounting Period (YYYY/MM)"
                placeholder="e.g. 2026/07"
              />
              <FieldError message={errors.accountPeriod?.message} />
            </RequiredFieldCell>
            <RequiredFieldCell>
              <DatePickerField
                name="receivedDate"
                label="Received Date"
                showError={false}
              />
              <FieldError message={errors.receivedDate?.message} />
            </RequiredFieldCell>

            <RequiredFieldCell>
              <DatePickerField
                name="transactionDate"
                label="Transaction Date"
                showError={false}
              />
              <FieldError message={errors.transactionDate?.message} />
            </RequiredFieldCell>
            <FieldCell>
              <TextInputField
                name="policyMonth"
                label="Policy Year/Month (YYYY/MM)"
                placeholder="e.g. 2026/07"
              />
              <FieldError message={errors.policyMonth?.message} />
            </FieldCell>

            <RequiredFieldCell>
              <SelectField
                name="transactionType"
                label="Transaction Type"
                options={transactionTypeOptions}
                // The lookup hook returns { label, value }; SelectInput reads
                // "text" by default, which would render every row blank.
                optionsTextField="label"
              />
              <FieldError message={errors.transactionType?.message} />
            </RequiredFieldCell>
            <RequiredFieldCell>
              <NumberInputFieldPricing
                name="policies"
                label="# Policies"
                prefix=""
                placeholder=""
                decimalScale={0}
              />
              <FieldError message={errors.policies?.message} />
            </RequiredFieldCell>

            <RequiredFieldCell>
              <NumberInputFieldPricing
                name="sIPolicies"
                label="# S.I. Policies"
                prefix=""
                placeholder=""
                helperText="(simultaneous-issue carve-out)"
                decimalScale={0}
              />
              <FieldError message={errors.sIPolicies?.message} />
            </RequiredFieldCell>
            <FieldCell>
              <NumberInputFieldPricing
                name="liability"
                label="Liability"
                decimalScale={2}
                isZeroAllowed={true}
              />
              <FieldError message={errors.liability?.message} />
            </FieldCell>

            <FieldCell>
              <NumberInputFieldPricing
                name="sILiability"
                label="S.I. Liability"
                decimalScale={2}
                isZeroAllowed={true}
                helperText="(required when # S.I. Policies > 0)"
              />
              <FieldError message={errors.sILiability?.message} />
            </FieldCell>
            <FieldCell>
              <NumberInputFieldPricing
                name="grossPremium"
                label="Gross Premium"
                decimalScale={2}
                isZeroAllowed={true}
                helperText="(gross premium including fees and taxes)"
              />
              <FieldError message={errors.grossPremium?.message} />
            </FieldCell>

            <FieldCell>
              <NumberInputFieldPricing
                name="remitBasis"
                label="Remit Basis"
                decimalScale={2}
                isZeroAllowed={true}
                helperText="(Risk Amount in Risk Rate states; Premium Amount otherwise)"
              />
              <FieldError message={errors.remitBasis?.message} />
            </FieldCell>
            <RequiredFieldCell>
              <NumberInputFieldPricing
                name="uwRetention"
                label="UW Retention"
                shouldDefaultToZero={false}
                decimalScale={2}
                isZeroAllowed={true}
                helperText="(Stewart's portion of the premium)"
              />
              <FieldError message={errors.uwRetention?.message} />
            </RequiredFieldCell>

            <FullWidthCell>
              <FormControlLabel
                control={<StewartCheckbox {...register("isAccrual")} />}
                label="Flag as accrual (recognized before cash is received)"
              />
            </FullWidthCell>

            <FullWidthCell>
              <FieldCell>
                <TextInputField
                  name="comments"
                  label="Comments"
                  placeholder="Optional context for this PR entry"
                  multiline
                  rows={2}
                />
                <FieldError message={commentsError} />
              </FieldCell>
            </FullWidthCell>
          </FieldsGrid>

          <DialogFooter>
            <ActionButton
              variant="outlined"
              color="secondary"
              onClick={handleClose}
            >
              Cancel
            </ActionButton>
            <ActionButton
              variant="contained"
              color="primary"
              onClick={handleSubmit(onSubmit)}
            >
              Add Premium Receivable Entry
            </ActionButton>
          </DialogFooter>
        </StyledDialogContent>
      </FormProvider>
    </StyledDialog>
  );
};

export default AddPremiumReceivablesModal;
